import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from 'components/ui/alert-dialog';

/*
 * A drop-in-shaped replacement for material-ui-confirm's useConfirm(), built on
 * shadcn's AlertDialog. Only 2 call sites use the real thing today
 * (admin/Database.jsx, admin/CollisionDetails.jsx) and aren't migrating this
 * phase, so this accepts (and ignores) the MUI-only styling props
 * (confirmationButtonProps, cancellationButtonProps, dialogProps,
 * dialogActionsProps) those call sites pass, so swapping the import later is a
 * one-line change without touching call-site options.
 */
export interface ConfirmOptions {
    title?: ReactNode;
    description?: ReactNode;
    confirmationText?: ReactNode;
    cancellationText?: ReactNode;
    /** material-ui-confirm compatibility -- accepted, not applied. */
    confirmationButtonProps?: Record<string, unknown>;
    cancellationButtonProps?: Record<string, unknown>;
    dialogProps?: Record<string, unknown>;
    dialogActionsProps?: Record<string, unknown>;
}

export type ConfirmFn = (options?: ConfirmOptions) => Promise<void>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

interface PendingConfirm {
    options: ConfirmOptions;
    resolve: () => void;
    reject: () => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [pending, setPending] = useState<PendingConfirm | null>(null);
    // material-ui-confirm rejects (rather than leaving unsettled) if the dialog is
    // dismissed without an explicit choice -- mirror that on unmount/replace too.
    const pendingRef = useRef<PendingConfirm | null>(null);
    pendingRef.current = pending;

    const confirm = useCallback<ConfirmFn>((options = {}) => {
        return new Promise<void>((resolve, reject) => {
            setPending({ options, resolve, reject });
        });
    }, []);

    const settle = (confirmed: boolean) => {
        const current = pendingRef.current;
        if (!current) return;
        setPending(null);
        if (confirmed) {
            current.resolve();
        } else {
            current.reject();
        }
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <AlertDialog
                open={pending !== null}
                onOpenChange={(open) => {
                    if (!open) settle(false);
                }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{pending?.options.title ?? 'Are you sure?'}</AlertDialogTitle>
                        {pending?.options.description ? (
                            <AlertDialogDescription>
                                {pending.options.description}
                            </AlertDialogDescription>
                        ) : null}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => settle(false)}>
                            {pending?.options.cancellationText ?? 'Cancel'}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={() => settle(true)}>
                            {pending?.options.confirmationText ?? 'Ok'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmContext.Provider>
    );
}

export function useConfirm(): ConfirmFn {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        throw new Error('useConfirm must be used within a <ConfirmProvider>');
    }
    return ctx;
}
